/**
 * Storage de tokens da auth interna (substitui supabase.auth.getSession).
 * Mantemos access + refresh em localStorage. O JWT é HS256, validado pelo
 * backend; aqui o front só precisa dele pra enviar Authorization.
 */

const KEY = 'myshow.auth.tokens';
// Backup da sessão do operador (master/parceiro) enquanto ele impersona um
// cliente. Fica em chave separada pra sobreviver ao clear() do access token
// de impersonação (que é curto e sem refresh).
const BACKUP_KEY = 'myshow.auth.backup';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;          // ISO8601
  refreshExpiresAt: string;   // ISO8601
}

type Listener = (tokens: StoredTokens | null) => void;
const listeners = new Set<Listener>();

function read(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

function write(t: StoredTokens | null) {
  if (t) localStorage.setItem(KEY, JSON.stringify(t));
  else localStorage.removeItem(KEY);
  listeners.forEach((fn) => fn(t));
}

export const authTokens = {
  getAccessToken(): string | null {
    return read()?.accessToken ?? null;
  },
  getRefreshToken(): string | null {
    return read()?.refreshToken ?? null;
  },
  getAll(): StoredTokens | null {
    return read();
  },
  setTokens(t: { accessToken: string; refreshToken: string; expiresAt: string; refreshExpiresAt: string }) {
    write({
      accessToken: t.accessToken,
      refreshToken: t.refreshToken,
      expiresAt: t.expiresAt,
      refreshExpiresAt: t.refreshExpiresAt,
    });
  },
  clear() {
    write(null);
  },

  // ---- Impersonação ----
  /** Guarda a sessão atual e instala o token de impersonação (sem refresh). */
  beginImpersonation(accessToken: string, expiresAt: string) {
    const current = read();
    if (current) localStorage.setItem(BACKUP_KEY, JSON.stringify(current));
    write({ accessToken, refreshToken: '', expiresAt, refreshExpiresAt: expiresAt });
  },
  /** Restaura a sessão do operador. Retorna true se havia backup. */
  endImpersonation(): boolean {
    const raw = localStorage.getItem(BACKUP_KEY);
    if (!raw) return false;
    try {
      write(JSON.parse(raw) as StoredTokens);
      localStorage.removeItem(BACKUP_KEY);
      return true;
    } catch {
      localStorage.removeItem(BACKUP_KEY);
      return false;
    }
  },
  hasImpersonationBackup(): boolean {
    return localStorage.getItem(BACKUP_KEY) != null;
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
